import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import "./module.scss";

const vendorOptions = ["All Vendors", "Hikari House", "Green Bowl", "Daily Bakery", "Urban Cafe"];
const vendorStatuses = ["All Status", "Active", "Inactive", "Pending Setup"];
const vendorAreas = ["All Areas", "Kuching", "Miri", "Sibu"];

const orderStatuses = [
  "All Status",
  "Pending",
  "Paid",
  "Collectible",
  "Collected",
  "Cancelled",
  "Refunded",
];

const config = {
  orders: {
    title: "Orders",
    subtitle: "Monitor guest checkout orders, payment status, and collection progress.",
    action: "Export Orders",
    columns: ["Order No", "Vendor", "Customer", "Status", "Collection Time", "Amount"],
    rows: [
      ["SB-10084", "Hikari House", "Channie / 012-3456789", "Collectible", "Today, 6:30 PM", "RM18.90"],
      ["SB-10083", "Green Bowl", "Daniel / 011-2233445", "Paid", "Today, 7:00 PM", "RM12.50"],
      ["SB-10082", "Daily Bakery", "Mei Ling / 016-9988776", "Collected", "Today, 5:30 PM", "RM9.90"],
    ],
  },

  vendors: {
    title: "Vendors",
    subtitle: "Manage registered vendors, outlet information, and product status.",
    action: "+ Add Vendor",
    columns: ["Vendor", "Contact", "Location", "Status", "Product", "Orders Today"],
    rows: [
      ["Hikari House", "012-3456789", "Kuching", "Active", "1 active", "12"],
      ["Green Bowl", "011-2233445", "Kuching", "Active", "1 active", "8"],
      ["Daily Bakery", "016-9988776", "Miri", "Pending Setup", "0 active", "0"],
      ["Urban Cafe", "019-7788990", "Sibu", "Inactive", "0 active", "0"],
    ],
  },

  administrators: {
    title: "Administrators",
    subtitle: "Manage administrator accounts and platform access.",
    action: "+ Add Administrator",
    columns: [
      "Name",
      "Email",
      "Mobile",
      "Status",
      "Last Login",
      "Actions",
    ],
    rows: [
      [
        "John Tan",
        "john@secondbite.my",
        "012-3456789",
        "Active",
        "Today, 9:25 AM",
        "Edit",
      ],
      [
        "Sarah Lim",
        "sarah@secondbite.my",
        "016-9988776",
        "Active",
        "Yesterday, 5:40 PM",
        "Edit",
      ],
      [
        "Daniel Wong",
        "daniel@secondbite.my",
        "011-2233445",
        "Inactive",
        "12 Jun 2026",
        "Edit",
      ],
    ],
  },

  settings: {
    title: "Settings",
    subtitle: "Manage platform configuration, payment gateway, and email notification settings.",
    action: "Save Settings",
    columns: ["Setting", "Description", "Value", "Status", "Actions"],
    rows: [
      ["General", "Platform name, support contact, and maintenance mode.", "SecondBite", "Enabled", "Edit"],
      ["Checkout", "Customer name, checkout timeout, and important notice.", "15 minutes", "Enabled", "Edit"],
      ["Payment Gateway", "Fiuu or 2C2P merchant configuration.", "Fiuu", "Enabled", "Edit"],
      ["Email Notification", "SMTP and automated email notification settings.", "Enabled", "Enabled", "Edit"],
      ["Order", "Unpaid order cancellation and pickup grace period.", "15 min auto cancel", "Enabled", "Edit"],
    ],
  },

  reports: {
    title: "Reports",
    subtitle: "View MVP sales and order performance reports.",
    action: "Export Report",
    columns: ["Report", "Description", "Date Range", "Total Orders", "Total Revenue", "Actions"],
    rows: [
      [
        "Sales Report",
        "Revenue summary by selected date range and vendor.",
        "Today",
        "32",
        "RM682.40",
        "View",
      ],
      [
        "Orders Report",
        "Order activity, collection status, cancellations, and refunds.",
        "Today",
        "32",
        "RM682.40",
        "View",
      ],
    ],
  },
};

const statusClass = (value) => String(value).toLowerCase().replace(/\s+/g, "-");
const adminRows = config.administrators.rows;
const slugify = (value) => String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const ModulePage = ({ type }) => {
  const navigate = useNavigate();
  const page = config[type] || config.orders;

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  const [vendor, setVendor] = useState("All Vendors");
  const [orderStatus, setOrderStatus] = useState("All Status");

  const [vendorStatus, setVendorStatus] = useState("All Status");
  const [vendorArea, setVendorArea] = useState("All Areas");
  const [keyword, setKeyword] = useState("");
  const [editingSetting, setEditingSetting] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);

  const [settingsForm, setSettingsForm] = useState({
    platformName: "SecondBite",
    supportEmail: "support@secondbite.my",
    supportPhone: "+60 12-3456789",
    platformStatus: "Active",
    maintenanceMessage: "We are temporarily closed for maintenance.",
    checkoutTimeout: "15",
    requireCustomerName: true,
    showImportantNotice: true,
    importantNotice: "Please collect your order within the selected pickup time.",
    paymentGateway: "Fiuu",
    merchantId: "SB_FIUU_DEMO",
    merchantKey: "********",
    verifySignature: true,
    sandboxMode: true,
    smtpHost: "smtp.gmail.com",
    smtpPort: "587",
    smtpUsername: "noreply@secondbite.my",
    smtpPassword: "********",
    fromName: "SecondBite",
    fromEmail: "noreply@secondbite.my",
    sendOrderConfirmation: true,
    sendWelcomeEmail: true,
    sendPasswordResetEmail: true,
    autoCancelMinutes: "15",
    pickupGracePeriod: "30",
    defaultOrderStatus: "Pending",
  });

  const updateSetting = (field, value) => {
    setSettingsForm((current) => ({ ...current, [field]: value }));
  };

  const reportDetails = {
    "Sales Report": {
      title: "Sales Report",
      subtitle: "Revenue summary by selected date range and vendor.",
      cards: [
        ["Total Revenue", "RM682.40"],
        ["Total Orders", "32"],
        ["Average Order", "RM21.33"],
        ["Refunded", "RM0.00"],
      ],
      sections: [
        {
          title: "Sales by Vendor",
          columns: ["Vendor", "Orders", "Gross Sales", "Refunds", "Net Sales"],
          rows: [
            ["Hikari House", "12", "RM226.80", "RM0.00", "RM226.80"],
            ["Green Bowl", "8", "RM100.00", "RM0.00", "RM100.00"],
            ["Daily Bakery", "7", "RM69.30", "RM0.00", "RM69.30"],
            ["Urban Cafe", "5", "RM286.30", "RM0.00", "RM286.30"],
          ],
        },
        {
          title: "Top Product Snapshot",
          columns: ["Product", "Vendor", "Daily Quantity", "Daily Sold", "Remaining"],
          rows: [
            ["Chicken Katsu Bento", "Hikari House", "18", "12", "6"],
            ["Salmon Rice Box", "Urban Cafe", "12", "5", "7"],
            ["Matcha Latte", "Daily Bakery", "24", "7", "17"],
          ],
        },
      ],
    },
    "Orders Report": {
      title: "Orders Report",
      subtitle: "Order activity, collection status, cancellations, and refunds.",
      cards: [
        ["Total Orders", "32"],
        ["Collected", "21"],
        ["Collectible", "7"],
        ["Cancelled / Timeout", "4"],
      ],
      sections: [
        {
          title: "Order Status Breakdown",
          columns: ["Status", "Orders", "Revenue", "Notes"],
          rows: [
            ["Paid", "4", "RM82.40", "Waiting for collection time"],
            ["Collectible", "7", "RM148.30", "Ready for pickup"],
            ["Collected", "21", "RM451.70", "Completed"],
            ["Cancelled", "2", "RM0.00", "Quantity restored"],
            ["Payment Timeout", "2", "RM0.00", "Quantity restored"],
          ],
        },
        {
          title: "Recent Orders",
          columns: ["Order No", "Vendor", "Customer", "Status", "Amount"],
          rows: [
            ["SB-10084", "Hikari House", "Channie", "Collectible", "RM18.90"],
            ["SB-10083", "Green Bowl", "Daniel", "Paid", "RM12.50"],
            ["SB-10082", "Daily Bakery", "Mei Ling", "Collected", "RM9.90"],
          ],
        },
      ],
    },
  };

  const handleAction = () => {
    switch (type) {
      case "vendors":
        navigate("/admin/vendors/create");
        break;

      case "administrators":
        navigate("/admin/administrators/create");
        break;

      case "orders":
        console.log("Export Orders");
        break;

      case "reports":
        console.log("Export Report");
        break;

      case "settings":
        setEditingSetting("General");
        break;
        
      default:
        break;
    }
  };

  const renderFilters = () => {
    if (type === "vendors") {
      return (
        <section className="module-page__filters">
          <label>
            <span>Vendor Status</span>
            <select value={vendorStatus} onChange={(event) => setVendorStatus(event.target.value)}>
              {vendorStatuses.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>City / Area</span>
            <select value={vendorArea} onChange={(event) => setVendorArea(event.target.value)}>
              {vendorAreas.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Keyword</span>
            <input
              type="text"
              placeholder="Search vendor name..."
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
            />
          </label>
        </section>
      );
    }

    if (type === "administrators") {
      return (
        <section className="module-page__filters">
          <label>
            <span>Status</span>
            <select>
              <option>All Status</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </label>

          <label>
            <span>Keyword</span>
            <input type="text" placeholder="Search name or email..." />
          </label>

          <div />
        </section>
      );
    }

    if (type === "settings") {
      return (
        <section className="module-page__filters">
          <label>
            <span>Setting Group</span>
            <select>
              <option>All Settings</option>
              <option>Platform</option>
              <option>Payment Gateway</option>
              <option>Email Notification</option>
              <option>Checkout</option>
            </select>
          </label>

          <label>
            <span>Keyword</span>
            <input type="text" placeholder="Search setting..." />
          </label>

          <div />
        </section>
      );
    }

    if (type === "reports") {
      return (
        <section className="module-page__filters module-page__filters--orders">
          <label>
            <span>Date Range</span>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </label>

          <label>
            <span>Vendor</span>
            <select value={vendor} onChange={(event) => setVendor(event.target.value)}>
              {vendorOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Report Type</span>
            <select>
              <option>All Reports</option>
              <option>Sales Report</option>
              <option>Orders Report</option>
            </select>
          </label>
        </section>
      );
    }

    return (
      <section className="module-page__filters module-page__filters--orders">
        <label>
          <span>Date Range</span>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </label>

        <label>
          <span>Vendor</span>
          <select value={vendor} onChange={(event) => setVendor(event.target.value)}>
            {vendorOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Order Status</span>
          <select value={orderStatus} onChange={(event) => setOrderStatus(event.target.value)}>
            {orderStatuses.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
      </section>
    );
  };

  const renderSwitch = (label, field) => (
    <label className="settings-modal__switch">
      <input
        type="checkbox"
        checked={Boolean(settingsForm[field])}
        onChange={(event) => updateSetting(field, event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );

  const renderSettingsEditor = () => {
    if (!editingSetting) return null;

    const titleMap = {
      General: "Edit General Settings",
      Checkout: "Edit Checkout Settings",
      "Payment Gateway": "Edit Payment Gateway",
      "Email Notification": "Edit Email Notification",
      Order: "Edit Order Settings",
    };

    return (
      <div className="settings-modal" role="presentation">
        <div className="settings-modal__backdrop" onClick={() => setEditingSetting(null)} />
        <section className="settings-modal__dialog" role="dialog" aria-modal="true">
          <div className="settings-modal__header">
            <div>
              <h2>{titleMap[editingSetting] || "Edit Setting"}</h2>
              <p>Static MVP form. Connect API later.</p>
            </div>
            <button type="button" className="settings-modal__close" onClick={() => setEditingSetting(null)}>
              ×
            </button>
          </div>

          <div className="settings-modal__body">
            {editingSetting === "General" && (
              <div className="settings-modal__grid">
                <label>
                  <span>Platform Name</span>
                  <input value={settingsForm.platformName} onChange={(event) => updateSetting("platformName", event.target.value)} />
                </label>
                <label>
                  <span>Support Email</span>
                  <input value={settingsForm.supportEmail} onChange={(event) => updateSetting("supportEmail", event.target.value)} />
                </label>
                <label>
                  <span>Support Phone</span>
                  <input value={settingsForm.supportPhone} onChange={(event) => updateSetting("supportPhone", event.target.value)} />
                </label>
                <label>
                  <span>Platform Status</span>
                  <select value={settingsForm.platformStatus} onChange={(event) => updateSetting("platformStatus", event.target.value)}>
                    <option>Active</option>
                    <option>Maintenance</option>
                  </select>
                </label>
                <label className="settings-modal__wide">
                  <span>Maintenance Message</span>
                  <textarea value={settingsForm.maintenanceMessage} onChange={(event) => updateSetting("maintenanceMessage", event.target.value)} />
                </label>
              </div>
            )}

            {editingSetting === "Checkout" && (
              <div className="settings-modal__grid">
                <label>
                  <span>Checkout Timeout (minutes)</span>
                  <input type="number" value={settingsForm.checkoutTimeout} onChange={(event) => updateSetting("checkoutTimeout", event.target.value)} />
                </label>
                <div className="settings-modal__stack">
                  {renderSwitch("Require Customer Name", "requireCustomerName")}
                  {renderSwitch("Show Important Notice", "showImportantNotice")}
                </div>
                <label className="settings-modal__wide">
                  <span>Important Notice</span>
                  <textarea value={settingsForm.importantNotice} onChange={(event) => updateSetting("importantNotice", event.target.value)} />
                </label>
              </div>
            )}

            {editingSetting === "Payment Gateway" && (
              <div className="settings-modal__grid">
                <label>
                  <span>Payment Gateway</span>
                  <select value={settingsForm.paymentGateway} onChange={(event) => updateSetting("paymentGateway", event.target.value)}>
                    <option>Fiuu</option>
                    <option>2C2P</option>
                  </select>
                </label>
                <label>
                  <span>Merchant ID</span>
                  <input value={settingsForm.merchantId} onChange={(event) => updateSetting("merchantId", event.target.value)} />
                </label>
                <label>
                  <span>Merchant Key</span>
                  <input value={settingsForm.merchantKey} onChange={(event) => updateSetting("merchantKey", event.target.value)} />
                </label>
                <div className="settings-modal__stack">
                  {renderSwitch("Verify Payment Signature", "verifySignature")}
                  {renderSwitch("Sandbox Mode", "sandboxMode")}
                </div>
              </div>
            )}

            {editingSetting === "Email Notification" && (
              <div className="settings-modal__grid">
                <label>
                  <span>SMTP Host</span>
                  <input value={settingsForm.smtpHost} onChange={(event) => updateSetting("smtpHost", event.target.value)} />
                </label>
                <label>
                  <span>SMTP Port</span>
                  <input value={settingsForm.smtpPort} onChange={(event) => updateSetting("smtpPort", event.target.value)} />
                </label>
                <label>
                  <span>SMTP Username</span>
                  <input value={settingsForm.smtpUsername} onChange={(event) => updateSetting("smtpUsername", event.target.value)} />
                </label>
                <label>
                  <span>SMTP Password</span>
                  <input value={settingsForm.smtpPassword} onChange={(event) => updateSetting("smtpPassword", event.target.value)} />
                </label>
                <label>
                  <span>From Name</span>
                  <input value={settingsForm.fromName} onChange={(event) => updateSetting("fromName", event.target.value)} />
                </label>
                <label>
                  <span>From Email</span>
                  <input value={settingsForm.fromEmail} onChange={(event) => updateSetting("fromEmail", event.target.value)} />
                </label>
                <div className="settings-modal__stack settings-modal__wide">
                  {renderSwitch("Send Order Confirmation", "sendOrderConfirmation")}
                  {renderSwitch("Send Welcome Email", "sendWelcomeEmail")}
                  {renderSwitch("Send Password Reset Email", "sendPasswordResetEmail")}
                  <button type="button" className="settings-modal__secondary">Test SMTP</button>
                </div>
              </div>
            )}

            {editingSetting === "Order" && (
              <div className="settings-modal__grid">
                <label>
                  <span>Auto Cancel Unpaid Order (minutes)</span>
                  <input type="number" value={settingsForm.autoCancelMinutes} onChange={(event) => updateSetting("autoCancelMinutes", event.target.value)} />
                </label>
                <label>
                  <span>Pickup Grace Period (minutes)</span>
                  <input type="number" value={settingsForm.pickupGracePeriod} onChange={(event) => updateSetting("pickupGracePeriod", event.target.value)} />
                </label>
                <label>
                  <span>Default Order Status</span>
                  <select value={settingsForm.defaultOrderStatus} onChange={(event) => updateSetting("defaultOrderStatus", event.target.value)}>
                    <option>Pending</option>
                    <option>Paid</option>
                    <option>Collectible</option>
                  </select>
                </label>
              </div>
            )}
          </div>

          <div className="settings-modal__footer">
            <button type="button" className="settings-modal__secondary" onClick={() => setEditingSetting(null)}>
              Cancel
            </button>
            <button type="button" className="settings-modal__primary" onClick={() => setEditingSetting(null)}>
              Save Setting
            </button>
          </div>
        </section>
      </div>
    );
  };


  const renderReportViewer = () => {
    if (!viewingReport) return null;

    const report = reportDetails[viewingReport] || reportDetails["Sales Report"];

    return (
      <div className="settings-modal report-modal" role="presentation">
        <div className="settings-modal__backdrop" onClick={() => setViewingReport(null)} />
        <section className="settings-modal__dialog report-modal__dialog" role="dialog" aria-modal="true">
          <div className="settings-modal__header">
            <div>
              <h2>{report.title}</h2>
              <p>{report.subtitle}</p>
            </div>
            <button type="button" className="settings-modal__close" onClick={() => setViewingReport(null)}>
              ×
            </button>
          </div>

          <div className="settings-modal__body report-modal__body">
            <div className="report-modal__meta">
              <span>Date Range: 28 Jun 2026 - 28 Jun 2026</span>
              <span>Vendor: {vendor}</span>
              <span>Generated: Today, 9:30 PM</span>
            </div>

            <div className="report-modal__cards">
              {report.cards.map(([label, value]) => (
                <article key={label} className="report-modal__card">
                  <span>{label}</span>
                  <strong>{value}</strong>
                </article>
              ))}
            </div>

            {report.sections.map((section) => (
              <section key={section.title} className="report-modal__section">
                <h3>{section.title}</h3>
                <div className="report-modal__table">
                  <div
                    className="report-modal__table-head"
                    style={{ gridTemplateColumns: `repeat(${section.columns.length}, minmax(120px, 1fr))` }}
                  >
                    {section.columns.map((column) => (
                      <span key={column}>{column}</span>
                    ))}
                  </div>
                  {section.rows.map((row, rowIndex) => (
                    <div
                      key={`${section.title}-${rowIndex}`}
                      className="report-modal__table-row"
                      style={{ gridTemplateColumns: `repeat(${section.columns.length}, minmax(120px, 1fr))` }}
                    >
                      {row.map((cell) => (
                        <span key={cell}>{cell}</span>
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="settings-modal__footer">
            <button type="button" className="settings-modal__secondary" onClick={() => setViewingReport(null)}>
              Close
            </button>
            <button type="button" className="settings-modal__primary" onClick={() => console.log("Export", report.title)}>
              Export Report
            </button>
          </div>
        </section>
      </div>
    );
  };

  return (
    <div className="module-page">
      <section className="module-page__header">
        <div>
          <p className="module-page__eyebrow">SecondBite Admin</p>
          <h2>{page.title}</h2>
          <p>{page.subtitle}</p>
        </div>

        <button type="button" onClick={handleAction}>
          {page.action}
        </button>
      </section>

      {renderFilters()}

      <section className="module-page__panel">
        <div className="module-page__panel-title">
          <h3>{page.title} Listing</h3>
          <p>Static MVP UI sample. Connect API later.</p>
        </div>

        <div className="module-page__table">
          <div
            className="module-page__table-head"
            style={{ gridTemplateColumns: `repeat(${page.columns.length}, minmax(140px, 1fr))` }}
          >
            {page.columns.map((column) => (
              <span key={column}>{column}</span>
            ))}
          </div>

          {page.rows.map((row, index) => (
            <button
              type="button"
              className={`module-page__table-row ${type === "orders" || type === "vendors" || type === "administrators" || type === "settings" || type === "reports" ? "module-page__table-row--clickable" : ""}`}
              style={{ gridTemplateColumns: `repeat(${page.columns.length}, minmax(140px, 1fr))` }}
              key={`${row[0]}-${index}`}
              onClick={() => {
                if (type === "orders") {
                  navigate(`/admin/orders/${row[0]}`);
                }
                if (type === "vendors") {
                  navigate(`/admin/vendors/${slugify(row[0])}`);
                }
                if (type === "administrators") {
                  navigate(`/admin/administrators/${slugify(row[0])}/edit`);
                }
                if (type === "settings") {
                  setEditingSetting(row[0]);
                }
                if (type === "reports") {
                  setViewingReport(row[0]);
                }
              }}
            >
              {row.map((cell, cellIndex) => (
                <span
                  key={`${cell}-${cellIndex}`}
                  className={
                    (cellIndex === 3 && type !== "orders") || cellIndex === 4
                      ? `module-page__status module-page__status--${statusClass(cell)}`
                      : cell === "Edit"
                        ? "module-page__action-text"
                        : ""
                  }
                >
                  {cell}
                </span>
              ))}
            </button>
          ))}
        </div>
      </section>

      {renderSettingsEditor()}
      {renderReportViewer()}
    </div>
  );
};

export default ModulePage;